import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {AnySlot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SlotStatus} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotStatus';
import {SlotFormula} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {SheetId, SlotId} from '@mintdata/mint-kit/api/models/Identifiers';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';

export abstract class AbstractSheetController
{
    public abstract getOrCreateSlot(sheetId:SheetId, slotLocation:SlotLocation):AnySlot;

    public abstract findSlotById(sheetId:SheetId, slotId:SlotId):AnySlot | null;

    public abstract findSlot(sheetId:SheetId, slotLocation:SlotLocation):AnySlot | null;

    public abstract getSlotFrameValue(sheetId:SheetId, slotLocation:SlotLocation):IFrame;

    public abstract getSlotStatus(sheetId:SheetId, slotLocation:SlotLocation):SlotStatus | null;

    public abstract isSlotFrameValue(sheetId:SheetId, slotLocation:SlotLocation):boolean;

    public abstract isAnySlotLoading(sheetId:SheetId):boolean;

    public abstract setSlotFormulas(sheetId:SheetId, slotLocations:Array<SlotLocation>, formulas:Array<SlotFormula>, formats:Array<string>):void;

    public abstract setSlotFormula(sheetId:SheetId, slotLocation:SlotLocation, formula:SlotFormula, guessFormat?:boolean):void;

    public abstract setSlotValue(sheetId:SheetId, slotLocation:SlotLocation, value:IValue):void

    public abstract makeSlotLazy(sheetId:SheetId, slotLocation:SlotLocation):void

    public abstract makeSlotRegular(sheetId:SheetId, slotLocation:SlotLocation):void

    public abstract clearSlot(sheetId:SheetId, slotLocation:SlotLocation):void;

    public abstract clearSlots(sheetId:SheetId, slotLocation:MeshRange):void;

    public abstract reEvaluateSlot(sheetId:SheetId, slotLocation:SlotLocation):Promise<boolean>;

    public abstract forceEvaluateSlot(sheetId:SheetId, slotLocation:SlotLocation):Promise<boolean>;

    public abstract reEvaluateLazySlot(sheetId:SheetId, slotLocation:SlotLocation, eventFrame?:IFrame):Promise<boolean>;

    public abstract abortSlotEvaluation(sheetId:SheetId, slotLocation:SlotLocation):void;

    public abstract insertColumns(sheetId:SheetId, fromColumnIndex:number, columnsCount:number):void;

    public abstract insertRows(sheetId:SheetId, fromRowIndex:number, rowsCount:number):void;

    public abstract insertCellsRight(sheetId:SheetId, fromColumnIndex:number, columnsCount:number):void;

    public abstract insertCellsDown(sheetId:SheetId, fromColumnIndex:number, columnsCount:number):void;

    public abstract deleteColumns(sheetId:SheetId, fromColumnIndex:number, columnsCount:number, keepBindings:boolean):void;

    public abstract deleteRows(sheetId:SheetId, fromRowIndex:number, rowsCount:number, keepBindings:boolean):void;

    public hasSlot(sheetId:SheetId, slotLocation:SlotLocation):boolean
    {
        const slot:AnySlot = this.findSlot(sheetId, slotLocation);

        return !!slot;
    }

    public getSlotFormula(sheetId:SheetId, slotLocation:SlotLocation):SlotFormula | null
    {
        const slot:AnySlot = this.findSlot(sheetId, slotLocation);

        return slot ? slot.formula : null;
    }

    public getSlotValue(sheetId:SheetId, slotLocation:SlotLocation):IValue | null
    {
        const slot:AnySlot = this.findSlot(sheetId, slotLocation);

        return slot ? slot.value : null;
    }

    public isSlotLoading(sheetId:SheetId, slotLocation:SlotLocation):boolean
    {
        const status = this.getSlotStatus(sheetId, slotLocation);

        return status && status.isEqualTo(SlotStatus.Loading);
    }

    public isSlotStreaming(sheetId:SheetId, slotLocation:SlotLocation):boolean
    {
        const status = this.getSlotStatus(sheetId, slotLocation);

        return status && status.isEqualTo(SlotStatus.Streaming);
    }
}
